import { RepositoryFactory } from '@/repositories/RepositoryFactory';
import _ from 'lodash';

const DebtRepository = RepositoryFactory.get('debts');
const InstallmentRepository = RepositoryFactory.get('installments');

/**
 * DebtInstallmentService - Business logic layer for debt installment operations
 * Handles data transformation, calculations, and API orchestration
 */
export default {
  /**
   * Get debt installment data with filtering and pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.pageSize - Items per page
   * @param {string} params.fromDate - Start date filter
   * @param {string} params.toDate - End date filter
   * @param {number} params.installmentId - Installment ID filter
   * @param {string} params.type - Transaction type
   * @returns {Promise<Object>} Formatted debt installment data
   */
  async getDebtInstallments(params = {}) {
    try {
      const response = await DebtRepository.getInstallmentDebts(params);
      return this.formatDebtInstallmentData(response);
    } catch (error) {
      console.error('Error in DebtInstallmentService.getDebtInstallments:', error);
      throw error;
    }
  },

  /**
   * Get installments for autocomplete dropdown
   * @returns {Promise<Array>} Formatted installment options
   */
  async getInstallmentsForAutocomplete() {
    try {
      const installments = await InstallmentRepository.getForAutocomplete();
      return installments;
    } catch (error) {
      console.error('Error in DebtInstallmentService.getInstallmentsForAutocomplete:', error);
      throw error;
    }
  },

  /**
   * Search installments by text
   * @param {string} searchText - Search text
   * @param {number} limit - Maximum results
   * @returns {Promise<Array>} Filtered installment options
   */
  async searchInstallments(searchText = '', limit = 200) {
    try {
      const results = await InstallmentRepository.search(searchText, limit);
      return results;
    } catch (error) {
      console.error('Error in DebtInstallmentService.searchInstallments:', error);
      throw error;
    }
  },

  /**
   * Format debt installment data for display
   * @param {Object} rawData - Raw API response data
   * @returns {Object} Formatted data
   */
  formatDebtInstallmentData(rawData) {
    return {
      dataset: rawData.dataset || [],
      total: rawData.total || 0,
      pages: rawData.pages || 1,
    };
  },

  /**
   * Calculate sum of a specific field in dataset
   * @param {Array} dataset - Data array
   * @param {string} fieldKey - Field to sum
   * @returns {number} Sum value
   */
  calculateSumAmount(dataset, fieldKey) {
    if (!Array.isArray(dataset) || !fieldKey) {
      return 0;
    }

    return _.reduce(
      dataset,
      (sum, item) => {
        const value = parseFloat(item[fieldKey]) || 0;
        return sum + value;
      },
      0
    );
  },

  /**
   * Calculate conditional sum (difference between two fields, only positive values)
   * @param {Array} dataset - Data array
   * @param {string} primaryKey - Primary field key
   * @param {string} secondaryKey - Secondary field key
   * @returns {number} Calculated sum
   */
  calculateConditionalSum(dataset, primaryKey, secondaryKey) {
    if (!Array.isArray(dataset) || !primaryKey || !secondaryKey) {
      return 0;
    }

    return _.reduce(
      dataset,
      (sum, item) => {
        const primaryValue = parseFloat(item[primaryKey]) || 0;
        const secondaryValue = parseFloat(item[secondaryKey]) || 0;
        const difference = primaryValue - secondaryValue;
        return sum + (difference > 0 ? difference : 0);
      },
      0
    );
  },

  /**
   * Calculate all summary totals for the debt installment data
   * @param {Array} dataset - Data array
   * @returns {Object} Summary totals
   */
  calculateSummaryTotals(dataset) {
    return {
      totalDebtAmount: this.calculateSumAmount(dataset, 'debtAmount'),
      totalPaidAmount: this.calculateSumAmount(dataset, 'paidAmount'),
      totalDebtSum: this.calculateConditionalSum(dataset, 'debtAmount', 'paidAmount'),
      totalPaidSum: this.calculateConditionalSum(dataset, 'paidAmount', 'debtAmount'),
    };
  },

  /**
   * Validate filter parameters
   * @param {Object} filters - Filter object
   * @returns {Object} Validated and cleaned filters
   */
  validateFilters(filters) {
    const cleanFilters = { ...filters };

    // Clean up installment selection
    if (cleanFilters.searchInstallment && cleanFilters.searchInstallment.trim() === '') {
      cleanFilters.installmentId = null;
    }

    // Ensure page is valid
    if (!cleanFilters.page || cleanFilters.page < 1) {
      cleanFilters.page = 1;
    }

    // Ensure pageSize is valid
    if (!cleanFilters.pageSize || cleanFilters.pageSize < 1) {
      cleanFilters.pageSize = 10;
    }

    return cleanFilters;
  },

  /**
   * Build API parameters from filter state
   * @param {Object} filters - Filter state
   * @param {Object} routeParams - Route parameters
   * @returns {Object} API parameters
   */
  buildApiParameters(filters, routeParams = {}) {
    const validatedFilters = this.validateFilters(filters);
    
    return {
      page: validatedFilters.page,
      pageSize: validatedFilters.pageSize,
      fromDate: validatedFilters.fromDate,
      toDate: validatedFilters.toDate,
      installmentId: validatedFilters.installmentId,
      type: routeParams.mode || routeParams.type,
    };
  },

  /**
   * Generate pagination link
   * @param {number} pageNum - Page number
   * @returns {string} Pagination link
   */
  generatePaginationLink(pageNum) {
    return pageNum === 1 ? '?' : `?page=${pageNum}`;
  },
};
